import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { refreshToken } from '../services/auth';
import config from '../config/config';

// Extend AxiosRequestConfig to include retry flag
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Event to notify all tabs/windows about logout
const logoutEvent = new Event('auth:logout');

/**
 * Canonical API client. All HTTP access should go through this instance —
 * api/axiosConfig.ts and api/index.ts re-export it.
 */
const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor — attach the current access token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.dispatchEvent(logoutEvent);
  window.location.href = '/login';
};

const handleRefreshError = (error: any) => {
  processQueue(error, null);
  forceLogout();
  return Promise.reject(error);
};

// Response interceptor — on 401, refresh once via the single-flight
// services/auth.refreshToken (concurrent 401s share one refresh request)
// and replay the original request with the new token.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError & { config: RetryConfig }) => {
    const originalRequest = error.config as RetryConfig;
    if (!originalRequest) return Promise.reject(error);

    // Rate limits don't invalidate the session
    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    // Never try to refresh in response to auth-endpoint failures
    const isAuthEndpoint = originalRequest.url?.includes('/auth/') ||
                          originalRequest.url?.includes('/register');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (typeof token === 'string') {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return api(originalRequest);
            }
            throw new Error('Invalid token received');
          })
          .catch((err) => handleRefreshError(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const tokenResponse = await refreshToken();
        if (!tokenResponse) throw new Error('Failed to refresh token');

        const { accessToken } = tokenResponse;
        processQueue(null, accessToken);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        return handleRefreshError(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
